import React, { useEffect, useState, useMemo } from "react";
import SelectOption from "./SelectOption";
import { ReactComponent as AddIcon } from "../../../Assets/Icons/addNewIcon.svg";
import { ReactComponent as InfoIcon } from "../../../Assets/Icons/info.svg";
import { ReactComponent as WhitePlainTick } from "../../../Assets/Icons/whiteplaintick.svg";
import { Tooltip } from "antd";
import Input from "../FormInputs";
import { useDispatch, useSelector } from "react-redux";
import { createOffer, createSubCategory } from "../../../services/offerService";
import { useFormik } from "formik";
import PrimaryButton from "../PrimaryButton";
import { subCategorySchema } from "../../../utils/validationSchemas";
import toast from "../../../utils/toast";
import { useOfferFormContext } from "./OfferFormContext";
import Checkbox from "antd/lib/checkbox/Checkbox";

const SubCategorySelection = ({
  categoryId,
  subCategories,
  setSubCategories,
  stepIndex,
  setStepIndex,
  handlePrevious,
  setPayloadData,
  editOfferData,
  isEdit,
  selectedMerchant,
  categories,
}) => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.offerReducer?.loading);
  const offerData = useSelector((state) => state?.offerReducer?.createdOffer);
  const role = useSelector(
    (state) => state?.authReducer?.tokenData?.user?.role
  );

  const { registerNextAction, setIsNextDisabled } = useOfferFormContext();

  // Get the selected category name for dynamic title
  const selectedCategoryName = useMemo(() => {
    // First try to get from editOfferData (edit mode)
    if (isEdit && editOfferData?.category?.title) {
      return editOfferData.category.title;
    }
    // Then try to get from offerData (new offer creation)
    if (offerData?.category?.title) {
      return offerData.category.title;
    }
    // Fallback: look up in categories array by categoryId
    if (categoryId && categories?.length > 0) {
      const category = categories.find((cat) => cat?._id === categoryId);
      return category?.title || "";
    }
    return "";
  }, [isEdit, editOfferData?.category?.title, offerData?.category?.title, categoryId, categories]);
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [selectedSubCategoriesIds, setSelectedSubCategoriesIds] = useState([]);
  const [addClicked, setAddClicked] = useState(false);
  const [newSubCategory, setNewSubCategory] = useState("");
  const [inputNonce, setInputNonce] = useState(0);
  const [subNames, setSubNames] = useState([]);
  const [offerPendingSub, setOfferPendingSub] = useState([]);
  const [isAddingSubCategory, setIsAddingSubCategory] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");

  const handleEditClick = (subCat) => {
    setEditingId(subCat._id);
    setEditValue(subCat.title);
  };

  const handleSaveEdit = (subCat) => {
    // Logic to save. For now, we update local lists to reflect change visually if ID matches.
    if (!editValue.trim()) return;

    setSubNames(prev => prev.map(n => n === subCat.title ? editValue : n));
    setFieldValue("subCategory", formik.values.subCategory.map(n => n === subCat.title ? editValue : n));

    // Update local lists with simple object update 
    const updateList = (list) => list.map(s => s._id === subCat._id ? { ...s, title: editValue } : s);
    setSubCategories(prev => updateList(prev));
    setOfferPendingSub(prev => updateList(prev));
    setSelectedSubCategories(prev => updateList(prev));

    setEditingId(null);
    setEditValue("");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const handleDeleteClick = (subCat) => {
    // Logic to delete local reference
    setSubCategories(prev => prev.filter(s => s._id !== subCat._id));
    setOfferPendingSub(prev => prev.filter(s => s._id !== subCat._id));
    setSelectedSubCategories(prev => prev.filter(s => s._id !== subCat._id));
    setSelectedSubCategoriesIds(prev => prev.filter(id => id !== subCat._id));
    setSubNames(prev => prev.filter(n => n !== subCat.title));
    const currentList = formik.values.subCategory || [];
    setFieldValue("subCategory", currentList.filter(n => n !== subCat.title));
  };

  useEffect(() => {
    // Logic to determine which data source to use:
    // 1. If we are editing, ordinarily we might look at editOfferData.
    // 2. BUT, if we have made changes in this session (persisted in offerData Redux state),
    //    and that offerData corresponds to the same offer ID, we should prioritize offerData.
    // 3. For new offers (!isEdit), we always use offerData.

    let sourceData = offerData;

    if (isEdit && editOfferData) {
      // If the Redux state (offerData) matches the offer being edited AND has potentially newer data, use it.
      // Otherwise, fall back to the initial editOfferData.
      if (offerData?._id === editOfferData?._id && Array.isArray(offerData?.subCategory)) {
        sourceData = offerData;
      } else {
        sourceData = editOfferData;
      }
    }

    // Reset relevant states before repopulating to avoid duplication
    setSelectedSubCategories([]);
    setSelectedSubCategoriesIds([]);
    setSubNames([]);
    setFieldValue("subCategory", []);
    setOfferPendingSub([]);

    if (sourceData?.subCategory) {
      sourceData.subCategory.forEach((s) => {
        if (s?.status === "pending" || s?.status === "rejected") {
          setOfferPendingSub((prev) => {
            const exists = prev.some(p => p._id === s._id);
            return exists ? prev : [...prev, s];
          });
        }
      });

      // Batch update state for stability
      const newSelected = [];
      const newIds = [];
      const newNames = [];

      sourceData.subCategory.forEach(s => {
        // Check if we already added it (dedupe)
        if (!newIds.includes(s._id)) {
          newSelected.push(s);
          newIds.push(s._id);
          newNames.push(s.title);
        }
      });

      setSelectedSubCategories(newSelected);
      setSelectedSubCategoriesIds(newIds);
      setSubNames(newNames);
      setFieldValue("subCategory", newNames);
    }
  }, [offerData?._id, editOfferData, isEdit]);

  // Normalize and split predefined vs custom for clear UI
  const normalizeTitle = (t) => String(t || "").trim().toLowerCase();
  const isCustomSub = (s) =>
    s?.isCustom === true || s?.merchantId || s?.merchantid || s?.merchant?._id;
  const uniqueByTitle = (arr) => {
    const seen = new Set();
    const out = [];
    for (const item of arr || []) {
      const key = normalizeTitle(item?.title);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(item);
    }
    return out;
  };

  const allApproved = uniqueByTitle(
    (subCategories || []).filter((sub) => sub?.status === "approved")
  );
  const allCustomApproved = uniqueByTitle(allApproved.filter(isCustomSub));
  const customTitles = new Set(allCustomApproved.map((s) => normalizeTitle(s.title)));
  const generalApproved = uniqueByTitle(
    allApproved.filter((s) => !customTitles.has(normalizeTitle(s.title)))
  );

  const initialValues = {
    subCategory: [],
  };

  const payload = {
    category: categoryId,
    merchantId: selectedMerchant?._id,
    subCategory: selectedSubCategoriesIds,
    id: offerData?._id,
    isCreated: false,
  };

  useEffect(() => {
    setPayloadData(payload);
  }, [categoryId, selectedSubCategoriesIds, offerData?._id]);

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: subCategorySchema,

    onSubmit: async (values) => {
      // If user started adding a custom sub-category, auto-save it before Next
      if (addClicked && (newSubCategory || "").trim().length > 0) {
        await handleNewSubCategorySubmit();
      }

      const hasRejectedSubCategory = selectedSubCategories?.some(
        (subCat) => subCat.status === "rejected"
      );
      if (hasRejectedSubCategory) {
        toast?.error("You cannot proceed with rejected sub category selected.");
        return;
      }
      const createPromise = await dispatch(
        createOffer({ ...payload, offerId: offerData?._id })
      )?.unwrap();
      if (createPromise?.success === true) {
        setStepIndex(stepIndex + 1);
      }
    },
  });

  const {
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    handleSubmit,
  } = formik;

  // Register the Next action
  useEffect(() => {
    registerNextAction(() => formik.handleSubmit());
  }, [registerNextAction, formik]);

  // Update disabled state
  useEffect(() => {
    const isDisabled = selectedSubCategoriesIds.length === 0;
    setIsNextDisabled(isDisabled);
  }, [selectedSubCategoriesIds, setIsNextDisabled]);

  // Helper to get exactly what is rendered
  const getRenderedItems = () => {
    const approved = (subCategories || []).filter((s) => s?.status === "approved");
    const pending = offerPendingSub || [];
    return [...approved, ...pending];
  };

  const isAllSelected = (() => {
    const allRendered = getRenderedItems();
    return allRendered?.length > 0 &&
      allRendered.every((subCat) => selectedSubCategoriesIds.includes(subCat?._id));
  })();

  const handleSelectAll = () => {
    const allRendered = getRenderedItems();

    // Check if everything visible is currently selected
    const allSelected = allRendered?.length > 0 &&
      allRendered.every((subCat) => selectedSubCategoriesIds.includes(subCat?._id));

    if (allSelected) {
      // DESELECT ALL visible items
      const updatedSubCategories = selectedSubCategories.filter(
        (subCat) => !allRendered.some((rendered) => rendered?._id === subCat?._id)
      );
      setSelectedSubCategories(updatedSubCategories);

      const updatedIds = selectedSubCategoriesIds.filter(
        (id) => !allRendered.some((rendered) => rendered?._id === id)
      );
      setSelectedSubCategoriesIds(updatedIds);

      // For titles/names, we must be careful not to remove a name if it's used by a selected item outside of this list (unlikely here but good practice)
      // Simpler approach: rebuild names from the new selected list
      const newNames = updatedSubCategories.map(s => s?.title);
      setSubNames(newNames);
      setFieldValue("subCategory", newNames);

    } else {
      // SELECT ALL visible items
      const newSubCategories = [...selectedSubCategories];
      const newIds = [...selectedSubCategoriesIds];
      // We don't rely on existing names state, we'll rebuild it to be safe or just append

      allRendered.forEach((subCat) => {
        if (!newIds.includes(subCat?._id)) {
          newSubCategories.push(subCat);
          newIds.push(subCat?._id);
        }
      });

      const newNames = newSubCategories.map(s => s?.title);

      setSelectedSubCategories(newSubCategories);
      setSelectedSubCategoriesIds(newIds);
      setSubNames(newNames);
      setFieldValue("subCategory", newNames);
    }
  };

  const handleClick = (id, title) => {
    const isSelected = selectedSubCategories.find(
      (subCat) => subCat?._id === id
    );

    if (isSelected) {
      const updatedSubCategories = selectedSubCategories.filter(
        (subCat) => subCat?._id !== id
      );
      setSelectedSubCategories(updatedSubCategories);

      const updatedIds = selectedSubCategoriesIds.filter(
        (subCategoryId) => subCategoryId !== id
      );
      setSelectedSubCategoriesIds(updatedIds);

      const updatedNames = subNames.filter((t) => t !== title);
      setSubNames(updatedNames);
      setFieldValue("subCategory", updatedNames);
    } else {
      const subCategory = subCategories?.find((subCat) => subCat?._id === id);
      setSelectedSubCategories((prev) => [...prev, subCategory]);
      setSelectedSubCategoriesIds((prev) => [...prev, subCategory?._id]);
      setSubNames((prev) => [...prev, subCategory?.title]);
      setFieldValue("subCategory", [...subNames, subCategory?.title]);
    }
  };

  const isSubCategoryClicked = (subCat) => {
    return selectedSubCategories.find(
      (selectedCat) => selectedCat?._id === subCat?._id
    );
  };

  const handleAddClick = () => {
    setNewSubCategory("");
    setInputNonce((n) => n + 1);
    setAddClicked(true);
  };

  const handleNewSubCategoryChange = (e) => {
    setNewSubCategory(e.target.value);
  };

  const handleNewSubCategorySubmit = async () => {
    const titleToCreate = (newSubCategory || "").trim();
    if (titleToCreate !== "") {
      setIsAddingSubCategory(true);
      try {
        let parentOfferId = offerData?._id || editOfferData?._id;

        // If no offer exists yet, we must create it first to attach the sub-category
        if (!parentOfferId) {
          const initialOfferPayload = {
            category: categoryId,
            merchantId: selectedMerchant?._id, // For Admin flow
            subCategory: [], // No subcategories yet
            isCreated: false,
          };

          const offerResponse = await dispatch(createOffer(initialOfferPayload))?.unwrap();
          if (offerResponse?.success) {
            parentOfferId = offerResponse?.data?._id;
            // Optionally update Redux or local state if not auto-handled,
            // but relying on the unwrapped response is safest for immediate use.
          } else {
            console.error("Failed to create initial offer for subcategory");
            toast.error("Failed to initialize offer. Please try again.");
            return;
          }
        }

        if (!parentOfferId) {
          toast.error("Unable to verify Offer ID.");
          return;
        }

        const payload = role === "merchant" ? {
          title: titleToCreate,
          status: true,
          category: categoryId,
          offer: parentOfferId,
          isCustom: true,
        } : {
          title: titleToCreate,
          status: true,
          category: categoryId,
          offer: parentOfferId,
          isCustom: true,
          merchantId: offerData?.merchant?._id || selectedMerchant?._id,
        };
        const createPromise = await dispatch(
          createSubCategory(payload)
        )?.unwrap();
        if (createPromise?.success === true) {
          const created = createPromise?.data || {};
          if (created?.status === "pending" || created?.status === "rejected") {
            setOfferPendingSub((prev) => {
              const exists = (prev || []).some((p) => p?._id === created?._id);
              return exists ? prev : [...(prev || []), created];
            });
          }
          const norm = (t) => String(t || "").trim().toLowerCase();
          setSubCategories((prev) => {
            const list = Array.isArray(prev) ? prev : [];
            const exists = list.some((s) => s?._id === created?._id || norm(s?.title) === norm(created?.title));
            return exists ? list : [...list, created];
          });
          setAddClicked(false);
          setSelectedSubCategories((prev) => {
            const list = Array.isArray(prev) ? prev : [];
            const exists = list.some((s) => s?._id === created?._id);
            return exists ? list : [...list, created];
          });
          setSelectedSubCategoriesIds((prev) => {
            const list = Array.isArray(prev) ? prev : [];
            const exists = list.includes(created?._id);
            return exists ? list : [...list, created?._id];
          });
          setSubNames((prev) => {
            const list = Array.isArray(prev) ? prev : [];
            const exists = list.includes(created?.title);
            return exists ? list : [...list, created?.title];
          });
          {
            const currentList = Array.isArray(formik.values?.subCategory)
              ? formik.values.subCategory
              : [];
            const exists = currentList.includes(created?.title);
            const nextList = exists ? currentList : [...currentList, created?.title];
            setFieldValue("subCategory", nextList);
          }
          setNewSubCategory("");
          setInputNonce((n) => n + 1);
        }
      } catch (error) {
        console.error("Error creating subcategory:", error);
      } finally {
        setIsAddingSubCategory(false);
      }
    }
  };



  const addButtonLabel = isAddingSubCategory ? "Adding..." : "Add";

  return (
    <div className="w-full flex flex-col items-center justify-start flex-grow">
      <div className="w-full max-w-[800px] mt-[30px] md:mt-[60px]">
        {/* Title */}
        <div className="mb-6 md:mb-8 w-full text-left">
          <h2 className="text-[22px] md:text-[32px] font-[600] text-[#303030] font-poppins text-left">
            Select the Subcategory for Your <span className="text-[#00ADEF]">{selectedCategoryName}</span> Offer
          </h2>
        </div>

        {/* Top Controls: Checkbox and Count */}
        <div className="flex items-center justify-between w-full mb-6">
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={handleSelectAll}
          >
            <div className={`w-[20px] h-[20px] rounded-[4px] border flex items-center justify-center transition-all ${isAllSelected ? "bg-[#00ADEF] border-[#00ADEF]" : "bg-white border-[#00ADEF]"
              }`}>
              {isAllSelected && <WhitePlainTick className="w-[12px] h-[12px] checkmark-pop" />}
            </div>
            <span className="text-[14px] font-[400] text-[#344054] font-poppins">
              Select All
            </span>
          </div>

          {selectedSubCategoriesIds.length > 0 && (
            <div className="text-[14px] text-[#767676] font-poppins">
              {selectedSubCategoriesIds.length} Selected
            </div>
          )}
        </div>

        {/* Grid of Subcategories */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full" data-field="subCategory">
          {subCategories
            ?.filter(s => s?.status === "approved" && !isCustomSub(s))
            ?.map((subCat) => (
              <SelectOption
                key={subCat?._id || subCat?.title}
                icon={null}
                title={subCat?.title}
                clicked={isSubCategoryClicked(subCat)}
                handleClick={() => handleClick(subCat?._id, subCat?.title)}
                postIcon={null}
                showCheckmark={true}
                noMargin={true}
              />
            ))}
        </div>

        {/* Empty State */}
        {(!subCategories?.some(s => s?.status === "approved") && offerPendingSub?.length === 0) && (
          <p className="font-poppins font-[500] text-[14px] text-[#303030] text-center my-8">
            The Selected Category does not have any sub category to select!!!
          </p>
        )}

        {/* Separator */}
        <div className="flex items-center w-full my-3">
          <div className="h-[1px] bg-[#E0E0E0] flex-grow"></div>
          <span className="px-4 text-[#767676] text-[14px] font-poppins">Or</span>
          <div className="h-[1px] bg-[#E0E0E0] flex-grow"></div>
        </div>

        {/* Custom Subcategories List */}
        {(() => {
          // Combine and Dedupe
          const allItems = [...(subCategories?.filter(s => s?.status === "approved" && isCustomSub(s)) || []), ...(offerPendingSub || [])];
          const uniqueCustoms = [];
          const seenIds = new Set();
          const seenTitles = new Set();

          allItems.forEach(item => {
            const normTitle = String(item.title || "").trim().toLowerCase();
            if (item._id && !seenIds.has(item._id)) {
              uniqueCustoms.push(item);
              seenIds.add(item._id);
              seenTitles.add(normTitle);
            } else if (!item._id && !seenTitles.has(normTitle)) {
              uniqueCustoms.push(item);
              seenTitles.add(normTitle);
            }
          });

          if (uniqueCustoms.length === 0) return null;

          return (
            <div className="w-full mb-4">
              <label className="block text-[#303030] text-[14px] font-[600] font-poppins mb-2">Custom Subcategory</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full">
                {uniqueCustoms.map(subCat => {
                  const isEditing = editingId === subCat._id;
                  const clicked = isSubCategoryClicked(subCat);

                  if (isEditing) {
                    return (
                      <div key={subCat._id} className="relative w-full h-[45px] flex items-center justify-between border border-[#D9D9D9] rounded-[5px] bg-white pr-3">
                        <div className="flex-grow h-full">
                          <Input
                            type="text"
                            value={editValue}
                            handleChange={(e) => setEditValue(e.target.value)}
                            placeholder="Enter name"
                            width="100%"
                            radius="0px"
                            height="43px"
                            InputDivClass="w-[100%] h-full border-none"
                            className="h-full border-none focus:ring-0 px-3"
                            handleKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveEdit(subCat);
                            }}
                          />
                        </div>
                        <span
                          onClick={() => handleSaveEdit(subCat)}
                          className="text-[#00ADEF] text-[12px] font-[500] font-poppins cursor-pointer select-none ml-2"
                        >
                          Save
                        </span>
                      </div>
                    )
                  } else {
                    return (
                      <div
                        key={subCat._id}
                        className={`relative border rounded-[5px] h-[45px] w-full flex items-center justify-between cursor-pointer transition-all ${clicked ? "bg-[#EBF9FE] border-[#00ADEF]" : "bg-white border-[#D9D9D9]"}`}
                        onClick={() => handleClick(subCat?._id, subCat?.title)}
                      >
                        <div className="flex items-center gap-3 ml-3 overflow-hidden">
                          <div className="flex items-center justify-center flex-shrink-0">
                            {clicked ? (
                              <div className="w-[18px] h-[18px] sm:w-[16px] sm:h-[16px] md:w-[18px] md:h-[18px] border border-[#00ADEF] rounded-[3px] bg-[#00ADEF] flex items-center justify-center">
                                <WhitePlainTick className="w-[12px] h-[12px] sm:w-[10px] sm:h-[10px] md:w-[12px] md:h-[12px] checkmark-pop" />
                              </div>
                            ) : (
                              <div className="w-[18px] h-[18px] sm:w-[16px] sm:h-[16px] md:w-[18px] md:h-[18px] border border-[#00ADEF] rounded-[3px] bg-white" />
                            )}
                          </div>
                          <span className={`text-[13px] font-poppins truncate ${clicked ? "text-[#00ADEF] font-[500]" : "text-[#767676] font-[400]"}`}>{subCat.title}</span>
                        </div>
                        <div className="flex items-center gap-3 mr-3 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                          <span onClick={() => handleEditClick(subCat)} className="text-[#00ADEF] text-[12px] font-[500] font-poppins cursor-pointer hover:underline">Edit</span>
                          <span onClick={() => handleDeleteClick(subCat)} className="text-[#00ADEF] text-[14px] font-[600] font-poppins cursor-pointer px-1 ml-1">X</span>
                        </div>
                      </div>
                    )
                  }
                })}
              </div>
            </div>
          )
        })()}

        {/* Logic for Add New or Input */}
        <div className="flex flex-col gap-4">
          {addClicked && (
            <div className="w-full">
              <label className="block text-[#303030] text-[14px] font-[600] font-poppins mb-2">
                Custom Subcategory
              </label>
              <div className="w-full max-w-[400px]">
                <Input
                  type="text"
                  placeholder="Pizza Shop"
                  width="100%"
                  radius="5px"
                  height="45px"
                  InputDivClass="w-[100%]"
                  name="newSubCategory"
                  key={inputNonce}
                  value={newSubCategory}
                  handleChange={handleNewSubCategoryChange}
                  handleKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleNewSubCategorySubmit();
                    }
                  }}
                />
              </div>

              {/* Duplicate Validation Message */}
              {(() => {
                const normNew = (newSubCategory || "").trim().toLowerCase();
                if (!normNew) return null;
                const exists = subCategories?.some(s => s.title.toLowerCase() === normNew) || offerPendingSub?.some(s => s.title.toLowerCase() === normNew);
                if (exists) {
                  return <span className="text-red-500 text-[12px] mt-1 block">This subcategory already exists.</span>;
                }
                return null;
              })()}

              <div className="flex items-center gap-4 mt-4">
                <button
                  onClick={() => setAddClicked(false)}
                  className="text-[#00ADEF] underline font-poppins text-[14px] font-[500]"
                >
                  Cancel
                </button>
                <div style={{ width: '80px' }}>
                  <PrimaryButton
                    className="h-[40px] rounded-[5px] text-[14px] font-[500] font-montserrat w-full"
                    label={addButtonLabel}
                    onClick={handleNewSubCategorySubmit}
                    disabled={
                      !newSubCategory.trim() ||
                      isAddingSubCategory ||
                      (subCategories?.some(s => s.title.toLowerCase() === (newSubCategory || "").trim().toLowerCase()) || offerPendingSub?.some(s => s.title.toLowerCase() === (newSubCategory || "").trim().toLowerCase()))
                    }
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-start gap-3 mt-2">
            <button
              onClick={!addClicked ? handleAddClick : undefined}
              disabled={addClicked}
              className={`h-[45px] rounded-[5px] flex items-center justify-center gap-2 px-5 transition-colors ${addClicked
                ? "bg-[#E0E0E0] cursor-not-allowed"
                : "bg-[#00ADEF] hover:bg-[#0099d6]"
                }`}
            >
              <div className={`w-[18px] h-[18px] rounded-full flex items-center justify-center ${addClicked ? "bg-[#BDBDBD]" : "bg-white"
                }`}>
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 1V11M1 6H11" stroke={addClicked ? "#E0E0E0" : "#00ADEF"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span className={`font-[500] font-poppins text-[14px] ${addClicked ? "text-[#767676]" : "text-white"
                }`}>
                Add another type of subcategory
              </span>
            </button>
            <Tooltip
              title="Add a custom sub-category that is not available in the list above."
              color="#00ADEF"
              placement="top"
              overlayInnerStyle={{ fontSize: '12px', padding: '8px 12px' }}
            >
              <div className="cursor-pointer flex items-center justify-center">
                <InfoIcon
                  className="w-[20px] h-[20px] info-icon-blue"
                />
              </div>
            </Tooltip>
          </div>
        </div>

        {errors?.subCategory && touched?.subCategory ? (
          <span className="text-red-600 sm:text-[10px] md:text-[12px] font-[600] ms-1 mt-4 block">
            {errors?.subCategory}
          </span>
        ) : null}

      </div>
    </div>
  );
};

export default SubCategorySelection;
